import { useState, useMemo } from 'react';

import {
    getMonthesNames,
    createMonth,
    getWeekDaysNames,
    getMonthNumberOfDays,
    createDate,
} from './CalendarItem';
import { getUserTimezone, zonedDateFromParts } from '../components/helper';

const DAYS_IN_WEEK = 7;

const getYearsInterval = (year) => {
    const startYear = Math.floor(year / 10) * 10;
    return [...Array(10)].map((_, index) => startYear + index);
};

const monthStart = (year, monthIndex, locale) =>
    createMonth({
        date: zonedDateFromParts(year, monthIndex, 1, getUserTimezone()),
        locale,
    });

export const useCalendar = ({
    locale = 'ru-RU',
    selectedDate: date,
    firstWeekDayNumber = 2,
}) => {
    const [mode, setMode] = useState('days');
    const [selectedDay, setSelectedDay] = useState(createDate({ date, locale }));
    const [endDay, setEndDay] = useState();
    const [selectedMonth, setSelectedMonth] = useState(
        monthStart(selectedDay.year, selectedDay.monthIndex, locale),
    );
    const [selectedYear, setSelectedYear] = useState(selectedDay.year);
    const [selectedYearsInterval, setSelectedYearsInterval] = useState(
        getYearsInterval(selectedDay.year),
    );

    const monthesNames = useMemo(() => getMonthesNames(locale), []);
    const weekDaysNames = useMemo(() => getWeekDaysNames(firstWeekDayNumber, locale), []);

    const days = useMemo(() => selectedMonth.createMonthDays(), [selectedMonth, selectedYear]);

    const calendarDays = useMemo(() => {
        const monthNumberOfDays = getMonthNumberOfDays(selectedMonth.monthIndex, selectedYear);

        const prevMonthDays = monthStart(
            selectedYear, selectedMonth.monthIndex - 1, locale,
        ).createMonthDays();

        const nextMonthDays = monthStart(
            selectedYear, selectedMonth.monthIndex + 1, locale,
        ).createMonthDays();

        const firstDay = days[0];
        const lastDay = days[monthNumberOfDays - 1];

        const shiftIndex = firstWeekDayNumber - 1;
        const numberOfPrevDays =
            firstDay.dayNumberInWeek - 1 - shiftIndex < 0
                ? DAYS_IN_WEEK - (firstWeekDayNumber - firstDay.dayNumberInWeek)
                : firstDay.dayNumberInWeek - 1 - shiftIndex;

        const numberOfNextDays =
            DAYS_IN_WEEK - lastDay.dayNumberInWeek + shiftIndex > 6
                ? DAYS_IN_WEEK - lastDay.dayNumberInWeek - (DAYS_IN_WEEK - shiftIndex)
                : DAYS_IN_WEEK - lastDay.dayNumberInWeek + shiftIndex;

        const totalCalendarDays = days.length + numberOfPrevDays + numberOfNextDays;

        const result = [];

        for (let i = 0; i < numberOfPrevDays; i += 1) {
            const inverted = numberOfPrevDays - i;
            result[i] = prevMonthDays[prevMonthDays.length - inverted];
        }

        for (let i = numberOfPrevDays; i < totalCalendarDays - numberOfNextDays; i += 1) {
            result[i] = days[i - numberOfPrevDays];
        }

        for (let i = totalCalendarDays - numberOfNextDays; i < totalCalendarDays; i += 1) {
            result[i] = nextMonthDays[i - totalCalendarDays + numberOfNextDays];
        }

        return result;
    }, [selectedMonth.year, selectedMonth.monthIndex, selectedYear]);

    const onClickArrow = (direction) => {
        const monthIndex =
            direction === 'left' ? selectedMonth.monthIndex - 1 : selectedMonth.monthIndex + 1;
        if (monthIndex === -1) {
            const year = selectedYear - 1;
            setSelectedYear(year);
            if (!selectedYearsInterval.includes(year)) setSelectedYearsInterval(getYearsInterval(year));
            return setSelectedMonth(monthStart(year, 11, locale));
        }

        if (monthIndex === 12) {
            const year = selectedYear + 1;
            setSelectedYear(year);
            if (!selectedYearsInterval.includes(year)) setSelectedYearsInterval(getYearsInterval(year));
            return setSelectedMonth(monthStart(year, 0, locale));
        }

        setSelectedMonth(monthStart(selectedYear, monthIndex, locale));
    };

    const setSelectedMonthByIndex = (monthIndex) => {
        setSelectedMonth(monthStart(selectedYear, monthIndex, locale));
    };

    return {
        state: {
            mode,
            calendarDays,
            weekDaysNames,
            monthesNames,
            selectedDay,
            selectedMonth,
            selectedYear,
            selectedYearsInterval,
            endDay,
        },
        functions: {
            onClickArrow,
            setMode,
            setSelectedDay,
            setSelectedMonthByIndex,
            setSelectedYear,
            setSelectedYearsInterval,
            setEndDay,
        },
    };
};
